import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isCategoryId } from '@dale/plans';

import { ApiError } from '../api/client';
import { completeBooking, listBookings, noShowBooking } from '../api/endpoints/bookings';
import { getRosterFloor } from '../api/endpoints/floorPlan';
import { reassignBookingTable } from '../api/endpoints/tables';
import type { Booking, CustomerProfile, PublicFloorResource } from '../api/schemas';
import { Drawer } from '../admin/components/Drawer';
import { ClassScheduler } from '../components/ClassScheduler';
import { CustomerFile } from '../components/CustomerFile';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState } from '../components/EmptyState';
import { FloorMap, type FloorMapMarker } from '../components/FloorMap';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { isCoach } from '../auth/roles';
import { usePermissions } from '../auth/usePermissions';
import { MisClasesPage } from './MisClases';
import { useSession } from '../auth/useSession';
import { bookingsQueryKey, rosterWindow } from '../lib/bookingWindows';
import { formatTime, isSameDay } from '../lib/format';
import { serviceName, useBusiness } from '../lib/useBusiness';
import { toolTitle } from '../lib/toolCopy';
import { useCapability } from '../lib/useCapability';
import { describeLevelMix, fileVocabulary, levelMix, profileSummary } from '../lib/customerFile';
import { useCustomerProfiles } from '../lib/useCustomerProfiles';

/** The front desk marks arrivals itself; 30 s is enough to catch a late booking. */
const BOOKINGS_REFETCH_INTERVAL_MS = 30_000;
const MAX_DAYS = 7;

/** Statuses that put a person in the room (or explain why they weren't). */
const ROSTER_STATUSES = new Set<Booking['status']>(['confirmed', 'completed', 'paid', 'no_show']);

type RosterMarker = FloorMapMarker & { resource: PublicFloorResource };

type MoveTarget = { booking: Booking } | null;

export function ClasesPage() {
  const { session } = useSession();
  return isCoach(session) ? <MisClasesPage /> : <StudioClasesPage />;
}

function StudioClasesPage() {
  const { session } = useSession();
  const business = useBusiness();
  const profiles = useCustomerProfiles();
  const graded = fileVocabulary(business?.category).levels;
  const { can } = usePermissions();
  // Espacios are part of the catalog door; the schedule has its own.
  const floorOpen = useCapability('mesas');
  const managesFloor = can('servicios') && floorOpen;
  const queryClient = useQueryClient();

  const bookingsRange = rosterWindow(new Date());
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(bookingsRange),
    queryFn: () => listBookings(bookingsRange),
    refetchInterval: BOOKINGS_REFETCH_INTERVAL_MS,
  });
  const floorQuery = useQuery({
    queryKey: ['roster-floor', session?.businessId, managesFloor],
    queryFn: () => getRosterFloor(session!.businessId, managesFloor),
    enabled: Boolean(session?.businessId),
  });

  const now = useMemo(() => new Date(), [bookingsQuery.dataUpdatedAt]);
  const roster = useMemo(
    () => (bookingsQuery.data ? buildRoster(bookingsQuery.data.data, now) : undefined),
    [bookingsQuery.data, now],
  );

  const [dayKey, setDayKey] = useState<string | undefined>(undefined);
  const [slotKey, setSlotKey] = useState<string | undefined>(undefined);
  const [openBooking, setOpenBooking] = useState<Booking | null>(null);
  const [moving, setMoving] = useState<MoveTarget>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const day = roster?.days.find((entry) => entry.key === dayKey) ?? roster?.days[0];
  const slot = day?.slots.find((entry) => entry.key === slotKey) ?? defaultSlot(day, now);

  const markers: RosterMarker[] = useMemo(
    () =>
      (floorQuery.data?.resources ?? []).map((resource) => ({
        id: resource.id,
        name: resource.name,
        seats: resource.seats,
        kind: resource.kind,
        gridX: resource.x,
        gridY: resource.y,
        gridW: resource.w,
        gridH: resource.h,
        resource,
      })),
    [floorQuery.data],
  );

  const bySpot = useMemo(() => {
    const map = new Map<string, Booking>();
    for (const booking of slot?.bookings ?? []) {
      if (booking.tableId) map.set(booking.tableId, booking);
    }
    return map;
  }, [slot]);
  const unassigned = (slot?.bookings ?? []).filter((booking) => !booking.tableId);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['business-bookings'] });
  };
  const checkIn = useMutation({
    mutationFn: ({ booking, kind }: { booking: Booking; kind: 'llego' | 'no-show' }) =>
      kind === 'llego'
        ? completeBooking(booking.id, crypto.randomUUID())
        : noShowBooking(booking.id, crypto.randomUUID()),
    onSuccess: () => {
      setOpenBooking(null);
      invalidate();
    },
    onError: (error) => setActionError(messageFor(error)),
  });
  const move = useMutation({
    mutationFn: ({ booking, tableId }: { booking: Booking; tableId: string }) =>
      reassignBookingTable(booking.id, tableId, crypto.randomUUID()),
    onSuccess: () => {
      setMoving(null);
      invalidate();
    },
    onError: (error) => setActionError(messageFor(error)),
  });

  const markerTap = (marker: RosterMarker) => {
    setActionError(null);
    const occupant = bySpot.get(marker.id);
    if (moving) {
      if (occupant) {
        setActionError(`${marker.name} ya está ocupado a esa hora.`);
        return;
      }
      move.mutate({ booking: moving.booking, tableId: marker.id });
      return;
    }
    if (occupant) setOpenBooking(occupant);
  };

  const started = openBooking ? new Date(openBooking.startsAt).getTime() <= now.getTime() : false;

  return (
    <main>
      <PageHeader
        title={toolTitle('clases', business && isCategoryId(business.category) ? business.category : undefined)}
        description="Programa tus sesiones en grupo, y mira quién viene y dónde se pone."
      />
      {business ? <ClassScheduler business={business} owner={can('clases')} resources={floorQuery.data?.resources} /> : null}
      {bookingsQuery.isLoading || floorQuery.isLoading ? (
        <LoadingState label="Cargando clases…" />
      ) : null}
      {bookingsQuery.isError ? <ErrorBanner message={messageFor(bookingsQuery.error)} /> : null}
      {floorQuery.isError ? <ErrorBanner message="No pudimos cargar el plano." /> : null}

      {roster && roster.days.length === 0 ? (
        <EmptyState
          title="Sin clases con gente todavía."
          description="Cuando alguien reserve una clase publicada, aparece aquí con su lugar."
        />
      ) : null}

      {roster && day ? (
        <>
          <div className="chip-row" role="tablist" aria-label="Día">
            {roster.days.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={entry.key === day.key}
                className="chip"
                data-status={entry.key === day.key ? 'active' : undefined}
                onClick={() => {
                  setDayKey(entry.key);
                  setSlotKey(undefined);
                  setMoving(null);
                }}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <div className="chip-row" role="tablist" aria-label="Hora de clase">
            {day.slots.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={entry.key === slot?.key}
                className="chip"
                data-status={entry.key === slot?.key ? 'active' : undefined}
                onClick={() => {
                  setSlotKey(entry.key);
                  setMoving(null);
                }}
              >
                {entry.label}
                <span className="chip-count">{entry.bookings.length}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {slot ? (
        <section className="section roster" aria-label={`Clase de ${slot.label}`}>
          <p className="row-sub roster-count">
            {bySpot.size} de {markers.length || '—'} lugares con gente
            {unassigned.length > 0 ? ` · ${unassigned.length} sin lugar` : ''}
          </p>
          {graded ? <LevelMixLine bookings={slot.bookings} profiles={profiles} /> : null}
          {moving ? (
            <p className="notice roster-move-hint" role="status">
              Toca un lugar libre para mover a {firstName(moving.booking)}.{' '}
              <button type="button" className="linklike" onClick={() => setMoving(null)}>
                Cancelar
              </button>
            </p>
          ) : null}
          {actionError ? <ErrorBanner message={actionError} /> : null}
          <FloorMap
            plan={floorQuery.data?.floorPlan ?? null}
            markers={markers}
            emptyState={
              markers.length === 0 ? (
                <p className="plan-empty">Sin plano todavía. Créalo en Espacios.</p>
              ) : undefined
            }
            markerDataAttrs={(marker) => {
              const occupant = bySpot.get(marker.id);
              return {
                'data-private': marker.resource.isPrivate || undefined,
                'data-occupied': occupant ? true : undefined,
                'data-checkin':
                  occupant && occupant.status !== 'confirmed' ? occupant.status : undefined,
                'data-move-target': moving && !occupant ? true : undefined,
              };
            }}
            renderMarkerExtra={(marker) => {
              const occupant = bySpot.get(marker.id);
              if (!occupant) return null;
              return (
                <span className="plan-occupant">
                  {firstName(occupant)}
                  {occupant.status === 'completed' ? ' ·' : null}
                  {occupant.status === 'no_show' ? ' ×' : null}
                </span>
              );
            }}
            isMarkerInteractive={(marker) => marker.kind !== 'bar'}
            onMarkerActivate={markerTap}
          />
        </section>
      ) : null}

      {slot && unassigned.length > 0 ? (
        <section className="section" aria-label="Sin lugar asignado">
          <h2 className="section-label">Sin lugar asignado</h2>
          <ul className="rows">
            {unassigned.map((booking) => (
              <li key={booking.id} className="row">
                <div className="grow">
                  <p className="row-title">{booking.customerName ?? 'Sin nombre'}</p>
                  <p className="row-sub">
                    {[
                      booking.customerId ? profileSummary(profiles.get(booking.customerId), graded) : null,
                      serviceName(business, booking.serviceId),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {managesFloor && markers.length > 0 ? (
                  <button
                    type="button"
                    className="btn small secondary"
                    onClick={() => {
                      setActionError(null);
                      setMoving({ booking });
                    }}
                  >
                    Asignar lugar
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Drawer
        open={Boolean(openBooking)}
        title={openBooking ? (openBooking.customerName ?? 'Sin nombre') : ''}
        onClose={() => setOpenBooking(null)}
      >
        {openBooking ? (
          <div className="roster-drawer">
            <p className="row-sub">
              {serviceName(business, openBooking.serviceId)} · {formatTime(openBooking.startsAt)}
              {spotNameFor(openBooking, markers) ? ` · ${spotNameFor(openBooking, markers)}` : ''}
            </p>
            {openBooking.customerPhone ? <p className="row-sub">{openBooking.customerPhone}</p> : null}
            {openBooking.status === 'completed' ? <p className="row-sub">Ya llegó.</p> : null}
            {openBooking.status === 'no_show' ? <p className="row-sub">No llegó.</p> : null}
            {openBooking.status === 'confirmed' ? (
              <div className="action-pair">
                <button
                  type="button"
                  className="btn"
                  disabled={checkIn.isPending}
                  onClick={() => checkIn.mutate({ booking: openBooking, kind: 'llego' })}
                >
                  Llegó
                </button>
                <button
                  type="button"
                  className="btn secondary"
                  disabled={checkIn.isPending || !started}
                  title={started ? undefined : 'Todavía no empieza esa reserva.'}
                  onClick={() => checkIn.mutate({ booking: openBooking, kind: 'no-show' })}
                >
                  No llegó
                </button>
              </div>
            ) : null}
            {managesFloor && openBooking.status === 'confirmed' ? (
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setActionError(null);
                  setMoving({ booking: openBooking });
                  setOpenBooking(null);
                }}
              >
                Mover de lugar
              </button>
            ) : null}
          </div>
        ) : null}
        {openBooking?.customerId ? <CustomerFile key={openBooking.customerId} customerId={openBooking.customerId} /> : null}
      </Drawer>
    </main>
  );
}

/** "Nivel: 3 principiantes · 1 avanzado" for the class on screen, once anyone is graded. */
function LevelMixLine({ bookings, profiles }: { bookings: Booking[]; profiles: ReadonlyMap<string, CustomerProfile> }) {
  const description = describeLevelMix(levelMix(bookings.map((booking) => booking.customerId), profiles));
  return description ? (
    <p className="row-sub class-level-mix roster-count">
      <span className="class-level-mix-label">Nivel</span> {description}
    </p>
  ) : null;
}

type RosterSlot = { key: string; label: string; startsAt: Date; bookings: Booking[] };
type RosterDay = { key: string; label: string; slots: RosterSlot[] };

/** Group roster-relevant bookings into day → class-time buckets, today first. */
export function buildRoster(bookings: Booking[], now: Date): { days: RosterDay[] } {
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const relevant = bookings
    .filter((booking) => ROSTER_STATUSES.has(booking.status))
    .filter((booking) => new Date(booking.startsAt).getTime() >= todayStart.getTime())
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const days: RosterDay[] = [];
  for (const booking of relevant) {
    const starts = new Date(booking.startsAt);
    const key = dayKeyOf(starts);
    let day = days.find((entry) => entry.key === key);
    if (!day) {
      if (days.length >= MAX_DAYS) continue;
      day = { key, label: dayLabel(starts, now), slots: [] };
      days.push(day);
    }
    const slotKey = booking.sessionId ?? booking.startsAt;
    let slot = day.slots.find((entry) => entry.key === slotKey);
    if (!slot) {
      slot = { key: slotKey, label: formatTime(booking.startsAt), startsAt: starts, bookings: [] };
      day.slots.push(slot);
    }
    slot.bookings = [...slot.bookings, booking];
  }
  return { days };
}

/** The slot people are arriving to: the first not-yet-finished one today, else the first. */
function defaultSlot(day: RosterDay | undefined, now: Date): RosterSlot | undefined {
  if (!day) return undefined;
  return (
    day.slots.find((slot) => slot.startsAt.getTime() >= now.getTime() - 60 * 60 * 1000) ??
    day.slots[day.slots.length - 1]
  );
}

function dayKeyOf(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(date: Date, now: Date): string {
  if (isSameDay(date, now)) return 'Hoy';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameDay(date, tomorrow)) return 'Mañana';
  return date.toLocaleDateString('es-PA', { weekday: 'short', day: 'numeric' });
}

function firstName(booking: Booking): string {
  return (booking.customerName ?? 'Sin nombre').split(' ')[0]!;
}

function spotNameFor(booking: Booking, markers: RosterMarker[]): string | undefined {
  if (!booking.tableId) return undefined;
  return markers.find((marker) => marker.id === booking.tableId)?.name;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso. Intenta de nuevo.';
}
